
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";

interface TemplateVersion {
  id: string;
  content: string;
  createdAt: string;
  templateId: string;
  version: number;
}

// Updated hook to query a real table
export const useTemplateVersioning = (originalTemplateId?: string) => {
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Load versions from localStorage temporarily since we don't have a real table yet
  const loadVersions = async (templateId?: string) => {
    if (!templateId && !originalTemplateId) return;
    
    const targetId = templateId || originalTemplateId;
    if (!targetId) return;

    setIsLoading(true);
    
    try {
      // Try to load from localStorage first
      const storedVersionsKey = `template_versions_${targetId}`;
      const storedVersions = localStorage.getItem(storedVersionsKey);
      
      if (storedVersions) {
        const parsed = JSON.parse(storedVersions);
        setVersions(parsed);
        
        // Set the current version to the latest one
        if (parsed.length > 0 && !currentVersionId) {
          setCurrentVersionId(parsed[0].id);
        }
        
        setIsLoading(false);
        return;
      }
      
      // No stored versions, initialize with empty array
      setVersions([]);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading template versions:', error);
      toast({
        title: "Fehler",
        description: "Versionshistorie konnte nicht geladen werden.",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const saveVersion = async (content: string, templateId?: string): Promise<TemplateVersion | null> => {
    if (!content || (!templateId && !originalTemplateId)) return null;
    
    const targetId = templateId || originalTemplateId;
    if (!targetId) return null;
    
    try {
      const storedVersionsKey = `template_versions_${targetId}`;
      const storedVersions = localStorage.getItem(storedVersionsKey);
      const existingVersions = storedVersions ? JSON.parse(storedVersions) : [];
      
      // Get next version number
      const nextVersion = existingVersions.length > 0 ? existingVersions[0].version + 1 : 1;
      
      // Create new version
      const newVersion: TemplateVersion = {
        id: `version-${Date.now()}`,
        content,
        createdAt: new Date().toISOString(),
        templateId: targetId,
        version: nextVersion
      };
      
      // Update local state and localStorage
      const updatedVersions = [newVersion, ...existingVersions];
      localStorage.setItem(storedVersionsKey, JSON.stringify(updatedVersions));
      
      // Update state
      setVersions(updatedVersions);
      setCurrentVersionId(newVersion.id);
      
      return newVersion;
    } catch (error) {
      console.error('Error saving template version:', error);
      toast({
        title: "Fehler",
        description: "Version konnte nicht gespeichert werden.",
        variant: "destructive"
      });
      return null;
    }
  };

  const getVersionContent = (versionId?: string): string | null => {
    if (!versionId) return null;
    const version = versions.find(v => v.id === versionId);
    return version?.content || null;
  };

  const getCurrentVersion = (): TemplateVersion | null => {
    if (!currentVersionId) return versions[0] || null;
    return versions.find(v => v.id === currentVersionId) || versions[0] || null;
  };

  const getPreviousVersions = (): TemplateVersion[] => {
    if (!currentVersionId) return versions.slice(1);
    const currentIndex = versions.findIndex(v => v.id === currentVersionId);
    if (currentIndex === -1) return versions.slice(1);
    return [
      ...versions.slice(0, currentIndex),
      ...versions.slice(currentIndex + 1)
    ];
  };

  // Load versions when templateId changes
  useEffect(() => {
    if (originalTemplateId) {
      loadVersions(originalTemplateId);
    }
  }, [originalTemplateId]);

  return {
    versions,
    currentVersionId,
    setCurrentVersionId,
    loadVersions,
    saveVersion,
    getVersionContent,
    getCurrentVersion,
    getPreviousVersions,
    isLoading
  };
};
